import React from 'react';
import Badge from '../ui/Badge';
import Icons from '../Icons';
import { statusLabels } from './inventoryUtils';
import { formatDate } from '../../utils/dateFormat';

const statusConfig = {
  available: { label: statusLabels.available, variant: 'active', icon: Icons.Check },
  checked_out: { label: statusLabels.checked_out, variant: 'review', icon: Icons.Clock },
  under_repair: { label: statusLabels.under_repair, variant: 'inactive', icon: Icons.Alert },
  retired: { label: statusLabels.retired, variant: 'inactive', icon: Icons.X }
};

const conditionVariants = {
  excellent: 'active',
  good: 'active',
  fair: 'review',
  poor: 'danger',
  damaged: 'danger',
  lost: 'inactive'
};

const getConditionVariant = (condition) => {
  const key = String(condition || '').toLowerCase();
  return conditionVariants[key] || 'default';
};

const RowMenu = ({ item, isOverdue, onView, onEdit, onReminder, canManage }) => {
  return (
    <details className="sp-row-menu" onClick={(event) => event.stopPropagation()}>
      <summary aria-label={`Actions for ${item.name}`}>
        <span aria-hidden="true">⋯</span>
      </summary>
      <div className="sp-row-menu__panel" role="menu">
        <button type="button" role="menuitem" onClick={() => onView?.(item)}>
          View details
        </button>
        {canManage && (
          <button type="button" role="menuitem" onClick={() => onEdit?.(item)}>
            Edit item
          </button>
        )}
        {canManage && isOverdue && (
          <button type="button" role="menuitem" onClick={() => onReminder?.(item)}>
            Send reminder
          </button>
        )}
      </div>
    </details>
  );
};

export default function InventoryTable({
  items,
  inventoryController,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  onViewItem,
  onEditItem,
  onSendReminder,
  canManage = true
}) {
  const visibleIds = items.map((item) => item.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  if (!items.length) {
    return (
      <div className="sp-panel sp-panel--muted p-6 text-sm sp-muted">
        No items match the selected filters.
      </div>
    );
  }

  return (
    <div className="sp-table sp-table--inventory-workspace" role="table" aria-label="Inventory items">
      <div className="sp-row sp-row--header" role="row">
        <div role="columnheader">
          <input
            type="checkbox"
            aria-label="Select all inventory items"
            checked={allSelected}
            onChange={() => onToggleSelectAll(allSelected)}
          />
        </div>
        <div role="columnheader">Item</div>
        <div role="columnheader">Type</div>
        <div role="columnheader">Status</div>
        <div role="columnheader">Assigned to</div>
        <div role="columnheader">Due</div>
        <div role="columnheader">Condition</div>
        <div role="columnheader" className="text-right">Actions</div>
      </div>
      {items.map((item) => {
        const activeAssignment = inventoryController.getActiveAssignment(item);
        const dueStatus = inventoryController.getDueStatus(item);
        const status = statusConfig[item.status] || statusConfig.available;
        const StatusIcon = status.icon;
        const isSelected = selectedIds.has(item.id);
        const dueLabel = activeAssignment?.dueDate ? formatDate(activeAssignment.dueDate) : '—';
        const dueHint = dueStatus.isOverdue
          ? `Overdue by ${Math.abs(dueStatus.daysRemaining || 0)} days`
          : dueStatus.isDueSoon
          ? `Due in ${dueStatus.daysRemaining} days`
          : '';
        return (
          <div
            key={item.id}
            className={`sp-row sp-row--inventory ${dueStatus.isOverdue ? 'sp-row--overdue' : ''} ${
              isSelected ? 'sp-row--selected' : ''
            }`}
            role="row"
            tabIndex={0}
            aria-selected={isSelected}
            onClick={() => onRowClick(item)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRowClick(item);
              }
            }}
          >
            <div role="cell" onClick={(event) => event.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected}
                aria-label={`Select ${item.name}`}
                onChange={() => onToggleSelect(item.id)}
              />
            </div>
            <div role="cell">
              <div className="sp-value" style={{ fontWeight: 600 }}>{item.name}</div>
              <div className="text-xs sp-muted">{item.reference}</div>
            </div>
            <div role="cell" className="text-sm sp-muted">{item.type}</div>
            <div role="cell" className="flex items-center gap-2 flex-wrap">
              <Badge variant={status.variant}>
                <span aria-hidden="true">
                  <StatusIcon />
                </span>
                {status.label}
              </Badge>
              {dueStatus.isOverdue && (
                <Badge variant="danger">
                  <Icons.Alert /> Overdue
                </Badge>
              )}
            </div>
            <div role="cell" className="text-sm sp-muted">
              {activeAssignment
                ? `${activeAssignment.assignedToName || activeAssignment.assignedToId} (${activeAssignment.assignedToType})`
                : 'Unassigned'}
            </div>
            <div role="cell" className="text-sm sp-muted">
              <div>{dueLabel}</div>
              {dueHint && <div className="text-xs sp-warning-text">{dueHint}</div>}
            </div>
            <div role="cell">
              <Badge variant={getConditionVariant(item.condition)}>{item.condition}</Badge>
            </div>
            <div role="cell" className="text-right">
              <RowMenu
                item={item}
                isOverdue={dueStatus.isOverdue}
                onView={onViewItem}
                onEdit={onEditItem}
                onReminder={onSendReminder}
                canManage={canManage}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
